import React from 'react';
import { Icon, IconNames } from 'components/atoms';
import { ICardsMap, CardPosition, ICard } from 'types';

import {
  CardSlot,
  Cards,
  CardGroup,
  CardRow,
  ResultValueBG,
  WinnerLabel,
  RotatedCardWrapper,
  SideLabel,
} from './styles';

interface IProps {
  cards: ICardsMap;
  winner: string | null;
  activeSlot?: CardPosition | null;
  hasUndefinedCard?: boolean;
}

export enum CardSlotVariants {
  Filled = 'filled',
  Empty = 'empty',
  Undefined = 'undefined',
}

export enum WinnerVariants {
  Banker = 'BANKER',
  Player = 'PLAYER',
  Tie = 'TIE',
}

export enum Sides {
  Banker = 'BANKER',
  Player = 'PLAYER',
}

export const BANKER_ORDER: CardPosition[] = ['6', '4', '2'];
export const PLAYER_ORDER: CardPosition[] = ['3', '1', '5'];

export const RED_SUITS = ['hearts', 'diamonds'];

const getRankIcon = (suit: string, rank: string) => {
  const color = RED_SUITS.includes(suit) ? 'red' : 'black';
  return `${color}_rank_${rank}` as IconNames;
};

export const ListCards = ({ cards, winner, hasUndefinedCard, activeSlot }: IProps) => {
  const renderSlot = (position: CardPosition, side: Sides) => {
    const card: ICard | undefined = cards[position];
    const isThirdCard = position === '5' || position === '6';
    const rotatedSide = isThirdCard ? (side === Sides.Banker ? 'left' : 'right') : undefined;

    const winnerHighlight = (): WinnerVariants | null => {
      if (winner === WinnerVariants.Tie) return WinnerVariants.Tie;
      if (winner === side) return winner as unknown as WinnerVariants;
      return null;
    };

    const renderCardContent = () => {
      if (card) {
        return (
          <CardSlot $variant={CardSlotVariants.Filled} $winnerHighlight={winnerHighlight()}>
            <Icon key="rank" name={getRankIcon(card.suit, card.rank)} />
            <Icon key="suit" name={card.suit as IconNames} />
          </CardSlot>
        );
      }

      if (hasUndefinedCard && activeSlot === position) {
        return (
          <CardSlot $variant={CardSlotVariants.Undefined}>
            <Icon name={IconNames.UndefinedCard} />
          </CardSlot>
        );
      }

      return <CardSlot $variant={CardSlotVariants.Empty} $active={activeSlot === position} />;
    };

    if (rotatedSide) {
      return (
        <RotatedCardWrapper key={position} $side={rotatedSide}>
          {renderCardContent()}
        </RotatedCardWrapper>
      );
    }

    return <React.Fragment key={position}>{renderCardContent()}</React.Fragment>;
  };

  return (
    <>
      <ResultValueBG name={IconNames.Background} />
      <Cards>
        {winner && (
          <WinnerLabel $winner={winner as WinnerVariants}>
            {winner} {winner !== WinnerVariants.Tie && `win`}
          </WinnerLabel>
        )}
        <CardGroup>
          <CardRow>{BANKER_ORDER.map((pos) => renderSlot(pos, Sides.Banker))}</CardRow>
          <SideLabel $align="left">Banker</SideLabel>
        </CardGroup>
        <CardGroup>
          <CardRow>{PLAYER_ORDER.map((pos) => renderSlot(pos, Sides.Player))}</CardRow>
          <SideLabel $align="right">Player</SideLabel>
        </CardGroup>
      </Cards>
    </>
  );
};
